import { fxRatesFormSchema } from '@dealstack/frontend/app/pages/Valuations/constants';
import { ValuationsLoaderContainer } from '@dealstack/frontend/app/pages/Valuations/styled';
import { FXRateHeader } from '@dealstack/frontend/app/pages/Valuations/views/EditValuation/FXRatesDrawer/components/FXRateHeader';
import { RateList } from '@dealstack/frontend/app/pages/Valuations/views/EditValuation/FXRatesDrawer/components/RateList';
import { GenericDrawer } from '@dealstack/frontend/shared/components/GenericDrawer/GenericDrawer';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import {
	TransValuations,
	useTranslationValuations,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	CurrencyType,
	CustomExchangeRate,
	ValuationWithClassifierRes,
} from '@dealstack/shared/lib/generated';
import { VALUATION_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@mantine/core';
import { DateTime } from 'luxon';
import { memo, useCallback, useEffect } from 'react';
import { Control, FormProvider, useForm, useFormContext } from 'react-hook-form';
import {
	FXRatesFormData,
	ValuationDetailsForm,
} from '@dealstack/frontend/app/pages/Valuations/constants.types';
import {
	ButtonContainer,
	DrawerContentContainer,
	FXRatesContainer,
	FXRatesFormFooterItem,
	RatesListFooter,
	RatesListFooterItemText,
	RatesListFooterLink,
	StatusDot,
} from './styled';

const FXRates = ({
	onClose,
	control,
	currentValuation,
	currencies,
	latestFxRates,
	updateCustomRates,
	isHistoricalAndCustomRatesFetching,
	isLatestFxRatesFetching,
	refetchCurrencies,
}: {
	onClose: () => void;
	control: Control<ValuationDetailsForm>;
	currentValuation: ValuationWithClassifierRes | undefined;
	currencies: CurrencyType[];
	latestFxRates: Partial<Record<CurrencyType, number>>;
	updateCustomRates: (data: { rates: CustomExchangeRate[] }) => Promise<void>;
	isHistoricalAndCustomRatesFetching: boolean;
	isLatestFxRatesFetching: boolean;
	refetchCurrencies: () => void;
}) => {
	const {
		formState: { isValid },
		getValues: getFXRatesFormValues,
	} = useFormContext<FXRatesFormData>();
	const tValuations = useTranslationValuations();

	useEffect(() => {
		// Refetch the currencies when the component mounts
		refetchCurrencies();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleUpdate = async () => {
		// Get all modified custom rates, and update the fx rates with them.
		const rates = Object.entries(getFXRatesFormValues('rates'))
			.filter(([_, rate]) => rate.custom)
			.map(
				([currency, rate]) =>
					({
						currency: currency as CurrencyType,
						value: Number(rate.custom),
					}) satisfies CustomExchangeRate
			);

		onClose();
		await updateCustomRates({ rates });
	};

	// TODO: Handle loading state
	if (isHistoricalAndCustomRatesFetching || isLatestFxRatesFetching) {
		return (
			<ValuationsLoaderContainer>
				<InlineLoader withoutBackground />
			</ValuationsLoaderContainer>
		);
	}

	const today = DateTime.now().toFormat('dd/MM/yyyy');
	const valuationDate = currentValuation?.valuationDate
		? DateTime.fromISO(currentValuation.valuationDate, { zone: 'utc' }).toFormat('dd/MM/yyyy')
		: tValuations('fx_drawer.fallback_date');

	const isFutureValuation =
		currentValuation?.valuationDate &&
		DateTime.fromISO(currentValuation?.valuationDate).toUTC().startOf('day') >
			DateTime.now().toUTC().startOf('day');

	return (
		<DrawerContentContainer>
			<FXRatesContainer>
				<FXRateHeader control={control} />
				<RateList
					defaultCurrency={currentValuation?.defaultCurrency}
					currencies={currencies}
					latestFxRates={latestFxRates}
				>
					<RatesListFooter>
						<FXRatesFormFooterItem>
							<StatusDot $status="live" />
							<RatesListFooterItemText variant="sans/14/400">
								<TransValuations i18nKey="fx_drawer.live_rate_info">
									Live rate for {{ date: today }} by&nbsp;
									<RatesListFooterLink
										href="https://api-ninjas.com/"
										target="_blank"
										rel="noopener noreferrer"
									>
										api-ninjas.com
									</RatesListFooterLink>
								</TransValuations>
							</RatesListFooterItemText>
						</FXRatesFormFooterItem>
						<FXRatesFormFooterItem>
							<StatusDot $status="valuation" />
							<RatesListFooterItemText variant="sans/14/400">
								{isFutureValuation
									? tValuations('fx_drawer.future_valuation_rate_info')
									: tValuations('fx_drawer.valuation_rate_info', {
											date: valuationDate,
										})}
							</RatesListFooterItemText>
						</FXRatesFormFooterItem>
						<FXRatesFormFooterItem>
							<StatusDot $status="modified" />
							<RatesListFooterItemText variant="sans/14/400">
								{tValuations('fx_drawer.modified_rate_info')}
							</RatesListFooterItemText>
						</FXRatesFormFooterItem>
					</RatesListFooter>
				</RateList>
			</FXRatesContainer>
			<ButtonContainer>
				<Button
					data-testid={VALUATION_ELEMENT_ID.FX_EXCHANGE_DRAWER_SAVE_BUTTON}
					variant="filled"
					color="blue"
					onClick={handleUpdate}
					disabled={!isValid}
				>
					{tValuations('fx_drawer.update_button')}
				</Button>
				<Button
					data-testid={VALUATION_ELEMENT_ID.FX_EXCHANGE_DRAWER_CANCEL_BUTTON}
					variant="subtle"
					onClick={onClose}
				>
					{tValuations('fx_drawer.cancel_button')}
				</Button>
			</ButtonContainer>
		</DrawerContentContainer>
	);
};

const MemoFXRates = memo(FXRates);

const FXRatesDrawer = ({
	opened,
	onClose,
	control,
	currentValuation,
	currencies,
	latestFxRates,
	historicalAndCustomFXRates,
	updateCustomRates,
	isHistoricalAndCustomRatesFetching,
	isLatestFxRatesFetching,
	refetchCurrencies,
}: {
	opened: boolean;
	onClose: () => void;
	control: Control<ValuationDetailsForm>;
	currentValuation: ValuationWithClassifierRes | undefined;
	currencies: CurrencyType[];
	latestFxRates: Partial<Record<CurrencyType, number>>;
	historicalAndCustomFXRates: Record<string, { history: number | null; custom: string | null }>;
	updateCustomRates: (data: { rates: CustomExchangeRate[] }) => Promise<void>;
	isHistoricalAndCustomRatesFetching: boolean;
	isLatestFxRatesFetching: boolean;
	refetchCurrencies: () => void;
}) => {
	const fxRatesForm = useForm<FXRatesFormData>({
		resolver: zodResolver(fxRatesFormSchema),
		defaultValues: {
			rates: {},
		},
		mode: 'onChange',
	});
	const { reset: resetForm } = fxRatesForm;
	const tValuations = useTranslationValuations();

	// Reset FX rates form when the API call is successful
	useEffect(() => {
		if (Object.keys(historicalAndCustomFXRates).length > 0) {
			resetForm({
				rates: historicalAndCustomFXRates,
			});
		}
	}, [historicalAndCustomFXRates, resetForm]);

	const handleClose = useCallback(() => {
		resetForm(); // Clean touched custom rates
		onClose();
	}, [resetForm, onClose]);

	return (
		<FormProvider {...fxRatesForm}>
			<GenericDrawer
				opened={opened}
				onClose={handleClose}
				title={<TextV2 variant="sans/25/600">{tValuations('fx_drawer.title')}</TextV2>}
				body={
					<MemoFXRates
						onClose={handleClose}
						control={control}
						currentValuation={currentValuation}
						currencies={currencies}
						latestFxRates={latestFxRates}
						updateCustomRates={updateCustomRates}
						isHistoricalAndCustomRatesFetching={isHistoricalAndCustomRatesFetching}
						isLatestFxRatesFetching={isLatestFxRatesFetching}
						refetchCurrencies={refetchCurrencies}
					/>
				}
			/>
		</FormProvider>
	);
};
const _FXRatesDrawerExport = memo(FXRatesDrawer);

export { _FXRatesDrawerExport as FXRatesDrawer };
